import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import ReactSelect from 'react-select';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import styled, { css } from 'styled-components';

import { Section as SectionComponent } from './BenefitsIntroSlice';

import { transformProperties, fieldHasContent } from '@/src/domains/prismic/helpers';
import { defaultPrismicComponents } from '@/src/domains/prismic/render';
import { mobileBreakpoint, tabletBreakpoint } from '@/src/domains/shared/breakpoints';
import { Description as DescriptionComponent } from '@/src/views/CountryBenefits/ContentSlice';

const Section = styled(SectionComponent)`
  && {
    background-color: var(--colors-blank);
  }
`;

const Note = styled(DescriptionComponent)`
  && {
    & > p {
      margin-bottom: 20px;
      max-width: 800px;

      & > em {
        font-style: normal;
        color: var(--primaryColor);
      }
    }
  }
`;

const TitleRow = styled.div`
  display: flex;
  padding: 20px 0 40px 20px;
  border-bottom: 1px solid var(--colors-irisBlue);

  ${tabletBreakpoint(css`
    display: none;
  `)};
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: baseline;
  width: 40%;

  &:last-child {
    width: 60%;
  }

  ${tabletBreakpoint(css`
    width: 100%;
  `)};
`;

const Title = styled.div`
  ${({ theme }) => theme.typography.bodyLeadMedium};
  color: var(--primaryColor);
  max-width: 400px;

  & > * {
    font-weight: inherit;
    margin: inherit;
  }
`;

const IconWrapper = styled.div`
  max-width: 20px;
  margin-right: 10px;
`;

const FirstColumnDescription = styled(ColumnTitle)`
  && {
    align-items: flex-start;
  }
`;

const SecondColumnDescription = styled.div`
  width: 60%;

  ${tabletBreakpoint(css`
    width: 90%;
    padding-top: 20px;
    margin-top: 20px;
  `)};
`;

const TableRow = styled(TitleRow)`
  padding: 30px 20px 50px;
  scroll-margin-top: 100px;

  ${tabletBreakpoint(css`
    flex-direction: column;
    display: flex;
  `)};
`;

const BenefitTitle = styled(Title)`
  color: var(--colors-darkBlue);
  ${({ theme }) => theme.typography.titleMD};
  font-weight: 400;
  padding-top: 7px;

  ${tabletBreakpoint(css`
    margin-right: 7px;
  `)};
`;

const BenefitSubheading = styled.div`
  ${({ theme }) => theme.typography.bodySM};
  color: ${({ theme }) => theme.colors.darkBlue_75};
  margin-top: 5px;
`;

const BenefitIconWrapper = styled.div`
  max-width: 50px;
  margin-right: 15px;

  > img {
    max-width: 100%;
    height: auto;
  }
`;

const CountryDescription = styled.div`
  color: ${({ theme }) => theme.colors.darkBlue_75};

  ul,
  ol {
    padding-left: 25px;

    em {
      font-style: normal;
      ${({ theme }) => theme.typography.bodyMedium}
      color: var(--primaryColor);
    }

    li {
      margin-bottom: 15px;
    }

    li::marker {
      color: var(--primaryColor);
    }
  }
`;

const CountrySelector = styled.div`
  position: sticky;
  top: 100px;
  z-index: 50;
  margin-bottom: 32px;
`;
const BenefitTidbit = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  background: var(--colors-irisBlue);
  color: white;
  padding: 32px;
  border-radius: 16px;
  margin-top: -16px;
  margin-bottom: 64px;

  ${tabletBreakpoint(css`
    grid-template-columns: 1fr;
  `)}

  & > * {
    color: inherit;
  }
`;

const Tooltip = styled.span`
  position: absolute;
  visibility: hidden;
  display: inline;
  top: -10px;
  left: 50%;
  transform: translate(-50%, -100%);
  width: 320px;
  background: var(--colors-darkBlue);
  color: var(--colors-blank);
  padding: 16px;
  border-radius: 8px;
  z-index: 100;
  font-weight: 400;

  ${tabletBreakpoint(css`
    width: 180px;
    transform: translate(-22%, -100%);
    left: 0%;
  `)}

  ${mobileBreakpoint(css`
    display: none;
  `)}

  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: var(--colors-darkBlue) transparent transparent transparent;
  }
`;

const TooltipContent = styled.span`
  box-sizing: border-box;
  position: relative;
  cursor: default;
  font-weight: 700;

  &:hover {
    ${Tooltip} {
      visibility: visible;
    }
  }
`;

const tidbitComponent = {
  hyperlink: ({
    node: {
      data: { url, target },
    },
    children,
  }) => (
    <Button variant="solid" tone="inverted" href={url} target={target}>
      {children}
    </Button>
  ),
};
const benefitSubheadComponents = {
  hyperlink: ({
    node: {
      data: { url },
    },
    children,
  }) => {
    const tooltipcontent = url.split('tooltip:')[1];
    return (
      <TooltipContent>
        {children}
        <Tooltip>{tooltipcontent}</Tooltip>
      </TooltipContent>
    );
  },
};

export function BenefitsTableSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const { firstColumn, firstColumnIcon, secondColumn, secondColumnIcon, note } = primary;

  const router = useRouter();

  function getValueAsId(value) {
    return value.replace(/^The /, '').replace(/\s+/g, '-').toLowerCase();
  }

  function handleChange(option) {
    if (typeof window !== 'undefined') {
      const id = document.getElementById(option.value);
      router.push(`#${option.value}`, undefined, { shallow: true });
      id.scrollIntoView({ behaviour: 'smooth ', block: 'start' });
    }
  }

  const selectValues = fields
    .map(({ benefitTitle, entireEntryTidbit }) => {
      if (!entireEntryTidbit) {
        return {
          label: asText(benefitTitle),
          value: getValueAsId(asText(benefitTitle)),
        };
      }
      return undefined;
    })
    .filter((item) => item !== undefined);

  const fieldSortedByCountry = fields.sort((countryA, countryB) => {
    function modifyCountyName(name) {
      const pattern = /^The /;
      return name.replace(pattern, '');
    }

    if (countryA.entireEntryTidbit || countryB.entireEntryTidbit) {
      return 0;
    }
    if (
      modifyCountyName(countryA.benefitTitle[0].text) >
      modifyCountyName(countryB.benefitTitle[0].text)
    ) {
      return 1;
    }
    if (
      modifyCountyName(countryB.benefitTitle[0].text) >
      modifyCountyName(countryA.benefitTitle[0].text)
    ) {
      return -1;
    }
    return 0;
  });

  return (
    <Section id="benefits-table">
      <ContentContainer>
        <CountrySelector>
          <ReactSelect
            instanceId="benefits-country-finder"
            options={selectValues}
            onChange={handleChange}
            placeholder="Quick search..."
            styles={{
              indicatorSeparator: () => null,
              dropdownIndicator: (provided) => ({
                ...provided,
                color: 'var(--colors-lynch)',
              }),
              control: (provided, state) => ({
                ...provided,
                outline: state.isFocused ? 'var(--colors-moonRaker) auto 1px' : null,
                boxShadow: 0,
                fontSize: '18px',
                height: '64px',
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor:
                  state.isFocused || state.isSelected ? 'var(--newBrandColor)' : null,
                color: state.isFocused || state.isSelected ? 'var(--colors-blank)' : null,
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '400',
                lineHeight: '1.4',
                padding: '12px 32px',
                margin: '0px',
                border: 'none',
                ':active': {
                  ...provided[':active'],
                  backgroundColor: state.isSelected && 'var(--newBrandColor)',
                },
              }),
              placeholder: (provided) => ({
                ...provided,
                color: 'var(--colors-lynch)',
                fontSize: '18px',
              }),
              singleValue: (provided) => ({
                ...provided,
                color: 'var(---newBrandColor)',
                fontSize: '18px',
              }),
            }}
          />
        </CountrySelector>
        <TitleRow>
          <ColumnTitle>
            <IconWrapper>
              <Image
                src={firstColumnIcon?.url}
                alt={firstColumnIcon?.alt || ''}
                width={firstColumnIcon?.dimensions.width}
                height={firstColumnIcon?.dimensions.height}
                quality="100"
                loading="eager"
              />
            </IconWrapper>
            {fieldHasContent(firstColumn) && (
              <Title>
                <PrismicRichText field={firstColumn} />
              </Title>
            )}
          </ColumnTitle>
          <ColumnTitle>
            <IconWrapper>
              <Image
                src={secondColumnIcon?.url}
                alt={secondColumnIcon?.alt || ''}
                width={secondColumnIcon?.dimensions.width}
                height={secondColumnIcon?.dimensions.height}
                quality="100"
                loading="eager"
              />
            </IconWrapper>
            {fieldHasContent(secondColumn) && (
              <Title>
                <PrismicRichText field={secondColumn} />
              </Title>
            )}
          </ColumnTitle>
        </TitleRow>
        {fieldSortedByCountry.map(
          (
            {
              benefitTitle,
              benefitIcon,
              benefitSubheading,
              benefitSubhead,
              description,
              entireEntryTidbit,
            },
            index
          ) =>
            entireEntryTidbit ? (
              <BenefitTidbit key={index}>
                {fieldHasContent(benefitTitle) && (
                  <BenefitTitle>
                    <PrismicRichText field={benefitTitle} />
                  </BenefitTitle>
                )}
                {fieldHasContent(description) && (
                  <div>
                    <PrismicRichText field={description} components={tidbitComponent} />
                  </div>
                )}
              </BenefitTidbit>
            ) : (
              <TableRow key={index} id={getValueAsId(asText(benefitTitle))}>
                <FirstColumnDescription>
                  {benefitIcon && (
                    <BenefitIconWrapper>
                      <Image
                        src={benefitIcon?.url}
                        alt={benefitIcon?.alt || ''}
                        width={benefitIcon?.dimensions.width}
                        height={benefitIcon?.dimensions.height}
                        quality="100"
                      />
                    </BenefitIconWrapper>
                  )}
                  <div>
                    {fieldHasContent(benefitTitle) && (
                      <BenefitTitle>
                        <PrismicRichText field={benefitTitle} />
                      </BenefitTitle>
                    )}
                    {fieldHasContent(benefitSubheading) && (
                      <BenefitSubheading>{benefitSubheading}</BenefitSubheading>
                    )}
                    {fieldHasContent(benefitSubhead) && (
                      <BenefitSubheading>
                        <PrismicRichText
                          field={benefitSubhead}
                          components={benefitSubheadComponents}
                        />
                      </BenefitSubheading>
                    )}
                  </div>
                </FirstColumnDescription>
                <SecondColumnDescription>
                  {fieldHasContent(description) && (
                    <CountryDescription>
                      <PrismicRichText field={description} components={defaultPrismicComponents} />
                    </CountryDescription>
                  )}
                </SecondColumnDescription>
              </TableRow>
            )
        )}

        {fieldHasContent(note) && (
          <Note>
            <PrismicRichText field={note} />
          </Note>
        )}
      </ContentContainer>
    </Section>
  );
}
